import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthStatus } from '../types';
import { authService } from '../services/authService';
import { analytics } from '../services/analytics';

interface AuthContextType {
  user: User | null;
  status: AuthStatus;
  login: (user: User, preloadData?: boolean) => Promise<void>;
  logout: () => void;
  refreshUserProfile: () => Promise<void>; // Para actualizar perfil del usuario
  registerPreloadFunction?: (type: 'favorites' | 'cart', fn: (userId: number, googleId: string) => Promise<void>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING);
  
  // Referencias a contextos para precarga (se setean desde children)
  const preloadRefs = React.useRef<{
    preloadFavorites?: (userId: number, googleId: string) => Promise<void>;
    preloadCart?: (userId: number, googleId: string) => Promise<void>;
  }>({});

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setStatus(AuthStatus.AUTHENTICATED);
    } else {
      setStatus(AuthStatus.UNAUTHENTICATED);
    }
  }, []);

  const login = async (userData: User, preloadData = true) => {
    try {
      // Sincronizar con backend
      const backendUser = await authService.syncUser(userData);
      
      // Actualizar userData con ID del backend
      const updatedUser = {
        ...userData,
        userId: backendUser.id
      };
      
      setUser(updatedUser);
      setStatus(AuthStatus.AUTHENTICATED);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Track login event
      analytics.login('google');
      
      // PRECARGA: Cargar favoritos y carrito en background
      if (preloadData && backendUser.id) {
        const userId = backendUser.id;
        // Esperar un tick para que los contextos se inicialicen
        setTimeout(() => {
          if (preloadRefs.current.preloadFavorites) {
            preloadRefs.current.preloadFavorites(userId, userData.id);
          }
          if (preloadRefs.current.preloadCart) {
            preloadRefs.current.preloadCart(userId, userData.id);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error syncing user with backend:', error);
      // Fallback: login solo localmente
      setUser(userData);
      setStatus(AuthStatus.AUTHENTICATED);
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const logout = () => {
    analytics.logout(); // Track logout before clearing data
    
    setUser(null);
    setStatus(AuthStatus.UNAUTHENTICATED);
    localStorage.removeItem('user');
    // También limpiar datos del usuario específicos
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('cart_') || key.startsWith('favorites_')) {
        localStorage.removeItem(key);
      }
    });
  };

  const refreshUserProfile = async () => {
    // Placeholder para futuras actualizaciones de perfil
    // Aquí podríamos hacer una llamada al backend para actualizar datos del usuario
    console.log('Refreshing user profile...');
  };

  // Función para registrar preload functions desde otros contextos
  const registerPreloadFunction = (type: 'favorites' | 'cart', fn: (userId: number, googleId: string) => Promise<void>) => {
    if (type === 'favorites') {
      preloadRefs.current.preloadFavorites = fn;
    } else if (type === 'cart') {
      preloadRefs.current.preloadCart = fn;
    }
  };

  const contextValue = React.useMemo(() => ({
    user,
    status,
    login,
    logout,
    refreshUserProfile,
    registerPreloadFunction
  }), [user, status]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};